import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { AppConsts } from '@shared/commonHelper/AppConsts';

/**
 * Holds the tenant's current "Project"/"Projects" wording for the Custom Project Label
 * (Labels & Terminology) feature and exposes it in the four grammatical forms the UI needs
 * (singular/plural x Title/lowercase).
 *
 * Read everywhere through the inherited getters on AppComponentBase
 * (projectLabel / projectLabelLower / projectLabelPlural / projectLabelPluralLower) so templates
 * stay free of service wiring. Because those getters are re-evaluated on every change-detection
 * cycle, calling update() + an ApplicationRef.tick() re-renders every label binding live.
 *
 * Seeded at bootstrap from appSession.tenant and refreshed when a `tenant.label.updated` SignalR
 * event arrives. When the feature flag is off, always returns the default wording.
 */
@Injectable({ providedIn: 'root' })
export class TenantLabelService {
    static readonly DEFAULT_SINGULAR = 'Project';
    static readonly DEFAULT_PLURAL = 'Projects';

    private _singular = TenantLabelService.DEFAULT_SINGULAR;
    private _plural = TenantLabelService.DEFAULT_PLURAL;

    /**
     * Emits whenever the label actually changes, so non-binding consumers (AG-Grid column headers,
     * document titles, etc.) can refresh imperatively. Plain template bindings do not need this -
     * they update through normal change detection.
     */
    readonly labelChanged$ = new Subject<void>();

    /** Singular, Title case - e.g. "Project" / "Case". */
    get singular(): string {
        return AppConsts.customProjectLabelFeatureEnabled && this._singular
            ? this._singular
            : TenantLabelService.DEFAULT_SINGULAR;
    }

    /** Plural, Title case - e.g. "Projects" / "Cases". */
    get plural(): string {
        return AppConsts.customProjectLabelFeatureEnabled && this._plural
            ? this._plural
            : TenantLabelService.DEFAULT_PLURAL;
    }

    /** Singular, lowercase for mid-sentence use - e.g. "project" / "case". */
    get singularLower(): string {
        return this.singular.toLowerCase();
    }

    /** Plural, lowercase for mid-sentence use - e.g. "projects" / "cases". */
    get pluralLower(): string {
        return this.plural.toLowerCase();
    }

    /** True when the tenant is still on the default "Project"/"Projects" wording. */
    get isDefault(): boolean {
        return this.singular === TenantLabelService.DEFAULT_SINGULAR
            && this.plural === TenantLabelService.DEFAULT_PLURAL;
    }

    /**
     * Update the current label and notify subscribers. Blank/empty values fall back to the
     * defaults. No-ops (and stays quiet) when nothing changed.
     */
    update(singular: string | undefined, plural: string | undefined): void {
        const nextSingular = singular && singular.trim() ? singular.trim() : TenantLabelService.DEFAULT_SINGULAR;
        const nextPlural = plural && plural.trim() ? plural.trim() : TenantLabelService.DEFAULT_PLURAL;

        if (nextSingular === this._singular && nextPlural === this._plural) {
            return;
        }

        this._singular = nextSingular;
        this._plural = nextPlural;
        this.labelChanged$.next();
    }
}
